import React from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'

import { callGetApi } from 'core'
import {
  ScheduleALegalDocuments,
  getAdminExhibit1LegalDocuments,
  getAdminScheduleALegalDocuments,
} from 'core/application/adminDashboard/legalDocuments/benefitPlanDocuments'
import getFileUrl from 'core/application/commons/fileStorage/getFileUrl'
import { getSummaryOfBenefitLegalDocuments } from 'core/application/employeeDashboard/legalDocuments/benefitPlanDocuments'
import { LegalDocumentType } from 'core/domain/legalDocuments/legalDocuments'

import Card from 'common/components/dataDisplay/Card'

import { queryRequest } from '../../../common/RequestUtils'
import notification from '../../../common/components/feedback/Notification'

import './DocumentCard.less'

export interface DocumentCardProps {
  documentType: LegalDocumentType
  productClassId?: string
  planPeriodId?: string
  title: string
  linkName: string
  link?: string
  description: string | null
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  documentType,
  productClassId,
  planPeriodId,
  title,
  link,
  linkName,
  description,
}) => {
  const { t: translate } = useTranslation()

  const getDocumentUrl = async (filename: string) => {
    const result = await queryRequest(() => getFileUrl(filename))
    if (result) {
      return result
    } else {
      notification.open({
        type: 'error',
        message: translate('general.error-getting-files'),
      })
      return ''
    }
  }

  const getDynamicFile = async (fileUrl) => {
    const result = await queryRequest(() => callGetApi(fileUrl))
    if (result) {
      return result
    } else {
      notification.open({
        type: 'error',
        message: translate('general.error-getting-files'),
      })
      return ''
    }
  }

  const getScheduleADocumentUrl = async (planPeriodId?: string, productClassId?: string) => {
    let data: ScheduleALegalDocuments = {
      productClassId,
      planPeriodId,
    }
    const result = await queryRequest(() => getAdminScheduleALegalDocuments(data))
    if (result) {
      return result
    } else {
      notification.open({
        type: 'error',
        message: translate('general.error-getting-files'),
      })
      return ''
    }
  }

  const getExhibit1DocumentUrl = async (planPeriodId?: string) => {
    const result = await queryRequest(() => getAdminExhibit1LegalDocuments(planPeriodId))
    if (result) {
      return result
    } else {
      notification.open({
        type: 'error',
        message: translate('general.error-getting-files'),
      })
      return ''
    }
  }

  const getSummaryOfBenefitDocumentUrl = async (planPeriodId?: string) => {
    const result = await queryRequest(() => getSummaryOfBenefitLegalDocuments({ planPeriodId }))
    if (result) {
      return result
    } else {
      notification.open({
        type: 'error',
        message: translate('general.error-getting-files'),
      })
      return ''
    }
  }

  const downloadLink = async (documentUrl: string) => {
    if (documentUrl) {
      const downloadLink = document.createElement('a')
      const fileName = `${title}.pdf`
      downloadLink.href = 'data:application/octet-stream;charset=utf-16le;base64,' + documentUrl
      downloadLink.download = fileName
      downloadLink.click()
    }
  }

  const getFilesLink = () => {
    if (documentType === LegalDocumentType.DynamicFile) {
      return (
        <a
          onClick={async (e) => {
            e.stopPropagation()
            const documentUrl = await getDynamicFile(link)
            await downloadLink(documentUrl)
          }}
          target="_blank"
          className="link"
        >
          {linkName}
        </a>
      )
    }

    if (link || documentType === LegalDocumentType.StaticFile) {
      return (
        <a
          onClick={async (e) => {
            e.stopPropagation()
            const documentUrl = await getDocumentUrl(link)
            if (documentUrl) window.open(documentUrl, '_blank')
          }}
          target="_blank"
          className="link"
        >
          {linkName}
        </a>
      )
    }

    if (documentType === LegalDocumentType.ScheduleClassDocument) {
      return (
        <a
          onClick={async (e) => {
            e.stopPropagation()
            const documentUrl = await getScheduleADocumentUrl(planPeriodId, productClassId)
            await downloadLink(documentUrl)
          }}
          target="_blank"
          className="link"
        >
          {linkName}
        </a>
      )
    }

    if (documentType === LegalDocumentType.ExhibitDocument) {
      return (
        <a
          onClick={async (e) => {
            e.stopPropagation()
            const documentUrl = await getExhibit1DocumentUrl(planPeriodId)
            await downloadLink(documentUrl)
          }}
          target="_blank"
          className="link"
        >
          {linkName}
        </a>
      )
    }

    if (documentType === LegalDocumentType.SummaryBenefitsCoverageDocument) {
      return (
        <a
          onClick={async (e) => {
            e.stopPropagation()
            const documentUrl = await getSummaryOfBenefitDocumentUrl(planPeriodId)
            await downloadLink(documentUrl)
          }}
          target="_blank"
          className="link"
        >
          {linkName}
        </a>
      )
    }
  }

  return (
    <div className="mt-16" style={{ width: '100%' }}>
      {description ? (
        <Card
          title={<h5 className="summary_card_title legal-documents-text-decoration">{title}</h5>}
          className="summary_legal_document mt-8"
          extra={getFilesLink()}
        >
          <Row>
            <Col xs={24} md={24}>
              <h6 className="summary_description">{description}</h6>
            </Col>
          </Row>
        </Card>
      ) : (
        <div className="flex jc-space-between document-card-wrapper">
          <div>
            <h5>{title}</h5>
          </div>
          <div>{getFilesLink()}</div>
        </div>
      )}
    </div>
  )
}

export default DocumentCard
